import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, map, firstValueFrom, switchMap, forkJoin, of, catchError } from 'rxjs';
import { AuthService } from '../auth.service';
import { FiscalDocument } from './models/fiscal-document.interface';
import { CountryRetention } from './models/country-retention.interface';
import { Timestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FiscalDocumentsService {
  private readonly COLLECTION_NAME = 'fiscal-documents';
  private readonly RESIDENCE_CHANGES_COLLECTION = 'residence-change-requests';
  private readonly STORAGE_PATH = 'fiscal-documents';

  private readonly apiUrl = environment.api?.url || 'https://redsys.api.devtop.online';

  constructor(
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private authService: AuthService,
    private http: HttpClient
  ) {}

  /**
   * Obtiene todos los documentos fiscales ordenados por fecha de subida
   */
  getAllDocuments(): Observable<FiscalDocument[]> {
    return this.afs.collection<FiscalDocument>(this.COLLECTION_NAME, ref => 
      ref.orderBy('uploadDate', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Obtiene los documentos de un usuario específico
   */
  getUserDocuments(userId: string): Observable<FiscalDocument[]> {
    return this.afs.collection<FiscalDocument>(this.COLLECTION_NAME, ref => 
      ref.where('userId', '==', userId)
        .orderBy('uploadDate', 'desc')
    ).valueChanges({ idField: 'id' });
  }

  /**
   * Aprobar un documento con fecha de expiración
   */
  approveDocumentWithExpiry(
    documentId: string, 
    retentionPercentage: number, 
    expiryDate: Date,
    country: string
  ): Observable<void> {
    return from(this.afs.collection(this.COLLECTION_NAME)
      .doc(documentId)
      .update({
        status: 'approved',
        retentionPercentage: retentionPercentage,
        countryOfResidence: country,
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate),
        approvalDate: firebase.firestore.Timestamp.now(),
        rejectionReason: null
      }));
  }

  /**
   * Rechazar un documento (certificado / aportar certificado).
   * Solo actualiza el estado del documento fiscal. NO debe actualizar el retentionPercentage del usuario:
   * al rechazar un certificado se debe dejar el % que el usuario tenía aceptado; solo al rechazar un
   * cambio de residencia se aplica la retención General.
   */
  rejectDocument(documentId: string, rejectionReason: string): Observable<void> {
    return from(this.afs.collection(this.COLLECTION_NAME)
      .doc(documentId)
      .update({
        status: 'rejected',
        rejectionReason: rejectionReason,
        rejectionDate: firebase.firestore.Timestamp.now()
      }));
  }

  /**
   * Actualizar fecha de expiración
   */
  updateExpiryDate(documentId: string, expiryDate: Date): Observable<void> {
    return from(this.afs.collection(this.COLLECTION_NAME)
      .doc(documentId)
      .update({
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate)
      }));
  }

  /**
   * Actualizar porcentaje de retención del usuario.
   * - Empresa: en Empresa.Datos_Empresa.retentionPercentage
   * - Particular: en particular.retentionPercentage
   */
  updateUserRetentionPercentage(userId: string, retentionPercentage: number): Observable<void> {
    const userDoc = this.afs.collection('users').doc(userId);

    return userDoc.get().pipe(
      switchMap(snapshot => {
        const userData = snapshot.data() as any | undefined;
        const updates = this.buildUserRetentionUpdatePayload(userData, retentionPercentage);
        const writePromise = snapshot.exists
          ? userDoc.update(updates)
          : userDoc.set(updates, { merge: true });
        return from(writePromise);
      }),
      map(() => undefined)
    );
  }

  /**
   * Construye el objeto de actualización para la retención del usuario.
   * - Empresa: Empresa.Datos_Empresa.retentionPercentage
   * - Particular: particular.retentionPercentage
   */
  private buildUserRetentionUpdatePayload(userData: any | undefined, retentionPercentage: number, hasEmpresa?: boolean): Record<string, any> {
    if (hasEmpresa) {
      return { 'Empresa.Datos_Empresa.retentionPercentage': retentionPercentage };
    }
    return { 'particular.retentionPercentage': retentionPercentage };
  }

  /**
   * Obtener retenciones por país
   */
  getCountryRetentions(): Observable<CountryRetention[]> {
    return this.afs.collection<CountryRetention>('country-retentions')
      .valueChanges({ idField: 'id' });
  }

  /**
   * Establecer retención por país
   */
  setCountryRetention(
    countryCode: string, 
    countryName: string, 
    percentage: number
  ): Observable<void> {
    const retention: CountryRetention = {
      countryCode,
      countryName,
      percentage
    };

    return from(this.afs.collection('country-retentions')
      .doc(countryCode)
      .set(retention));
  }

  /**
   * Eliminar retención por país
   */
  deleteCountryRetention(countryName: string): Observable<void> {
    return from(this.afs.collection('country-retentions')
      .doc(countryName)
      .delete());
  }

  /**
   * Verificar certificados próximos a caducar y enviar correos
   * Incluye documentos fiscales y cambios de residencia
   */
  checkExpiringCertificates(): Observable<void> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    // Obtener documentos fiscales próximos a caducar
    const fiscalDocs$ = this.afs.collection<FiscalDocument>(this.COLLECTION_NAME, ref =>
      ref.where('status', 'in', ['expiring_soon', 'approved', 'change_approved'])
    ).get().pipe(
      map(snapshot => {
        return snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as FiscalDocument & { id: string }))
          .filter(doc => {
            if (!doc.expiryDate) return false;
            const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
              ? (doc.expiryDate as any).toDate() 
              : new Date(doc.expiryDate as any);
            expiryDate.setHours(0, 0, 0, 0);
            // Solo incluir los que están próximos a caducar pero aún no han caducado
            return expiryDate > today && expiryDate <= thirtyDaysFromNow;
          });
      })
    );

    // Obtener cambios de residencia próximos a caducar
    const residenceDocs$ = this.afs.collection<any>(this.RESIDENCE_CHANGES_COLLECTION, ref =>
      ref.where('status', 'in', ['expiring_soon', 'approved'])
    ).get().pipe(
      map(snapshot => {
        return snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(doc => {
            if (!doc.validityDate) return false;
            const validityDate = typeof doc.validityDate === 'object' && 'toDate' in doc.validityDate 
              ? (doc.validityDate as any).toDate() 
              : new Date(doc.validityDate as any);
            validityDate.setHours(0, 0, 0, 0);
            // Solo incluir los que están próximos a caducar pero aún no han caducado
            return validityDate > today && validityDate <= thirtyDaysFromNow;
          });
      })
    );

    return forkJoin([fiscalDocs$, residenceDocs$]).pipe(
      switchMap(([fiscalDocs, residenceDocs]) => {
        // Convertir cambios de residencia al formato de documentos fiscales para procesamiento
        const convertedResidenceDocs = residenceDocs.map((doc: any) => ({
          id: doc.id,
          userId: doc.userId,
          fileName: `Cambio de residencia a ${doc.requestedCountry || 'país no especificado'}`,
          expiryDate: doc.validityDate,
          userEmail: doc.userEmail,
          userName: doc.userName,
          documentType: 'residence_change' as 'residence_change'
        }));

        const allExpiringDocs = [...fiscalDocs, ...convertedResidenceDocs];
        
        console.log('📧 [DEBUG] Certificados próximos a caducar encontrados:', allExpiringDocs.length);
        console.log('📧 [DEBUG] - Documentos fiscales:', fiscalDocs.length);
        console.log('📧 [DEBUG] - Cambios de residencia:', residenceDocs.length);

        if (allExpiringDocs.length === 0) {
          return of(undefined);
        }

        const emailPromises = allExpiringDocs.map(async (doc) => {
          let userEmail = doc.userEmail;
          let userName = doc.userName;
          
          // Si no tiene email, intentar obtenerlo del usuario
          if (doc.userId && !userEmail) {
            try {
              const userDoc = await this.afs.collection('users').doc(doc.userId).get().toPromise();
              if (userDoc?.exists) {
                const userData = userDoc.data() as any;
                userEmail = userData?.email || userData?.userEmail;
                userName = userName || userData?.particular?.nombre || userData?.name || 'Usuario';
              }
            } catch (error) {
              console.error(`📧 [ERROR] Error al obtener datos del usuario ${doc.userId}:`, error);
            }
          }

          if (doc.userId && userEmail) {
            const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
              ? (doc.expiryDate as any).toDate() 
              : new Date(doc.expiryDate as any);
            
            return this.sendExpiryNotification(
              userEmail,
              userName || 'Usuario',
              doc.fileName || 'Documento fiscal',
              expiryDate
            );
          } else {
            console.warn(`📧 [WARN] Documento sin userId o userEmail:`, {
              docId: doc.id,
              userId: doc.userId,
              userEmail: userEmail
            });
            return Promise.resolve();
          }
        });

        return from(Promise.all(emailPromises)).pipe(
          map(() => undefined)
        );
      })
    );
  }

  /**
   * Verificar y actualizar documentos caducados
   * Incluye documentos fiscales y cambios de residencia
   */
  checkAndUpdateExpiredDocuments(): Observable<void> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Obtener documentos fiscales caducados
    const fiscalDocs$ = this.afs.collection<FiscalDocument>(this.COLLECTION_NAME, ref =>
      ref.where('status', 'in', ['approved', 'change_approved'])
    ).get().pipe(
      map(snapshot => {
        return snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as FiscalDocument & { id: string }))
          .filter(doc => {
            if (!doc.expiryDate) return false;
            const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
              ? (doc.expiryDate as any).toDate() 
              : new Date(doc.expiryDate as any);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate <= today;
          });
      })
    );

    // Obtener cambios de residencia caducados
    const residenceDocs$ = this.afs.collection<any>(this.RESIDENCE_CHANGES_COLLECTION, ref =>
      ref.where('status', 'in', ['approved', 'expiring_soon'])
    ).get().pipe(
      map(snapshot => {
        return snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(doc => {
            if (!doc.validityDate) return false;
            const validityDate = typeof doc.validityDate === 'object' && 'toDate' in doc.validityDate 
              ? (doc.validityDate as any).toDate() 
              : new Date(doc.validityDate as any);
            validityDate.setHours(0, 0, 0, 0);
            return validityDate <= today;
          });
      })
    );

    return forkJoin([fiscalDocs$, residenceDocs$]).pipe(
      switchMap(([fiscalDocs, residenceDocs]) => {
        console.log('📧 [DEBUG] Documentos fiscales caducados encontrados:', fiscalDocs.length);
        console.log('📧 [DEBUG] Cambios de residencia caducados encontrados:', residenceDocs.length);
        
        const batch = this.afs.firestore.batch();

        // Actualizar documentos fiscales caducados
        fiscalDocs.forEach(doc => {
          const docRef = this.afs.collection(this.COLLECTION_NAME).doc(doc.id).ref;
          batch.update(docRef, {
            status: 'expired',
            expiredDate: firebase.firestore.Timestamp.now()
          });
        });

        // Actualizar cambios de residencia caducados
        residenceDocs.forEach(doc => {
          const docRef = this.afs.collection(this.RESIDENCE_CHANGES_COLLECTION).doc(doc.id).ref;
          batch.update(docRef, {
            status: 'expired',
            expiredDate: firebase.firestore.Timestamp.now()
          });
        });

        // Ejecutar actualizaciones primero
        return from(batch.commit()).pipe(
          switchMap(async () => {
            const notifications: Promise<void>[] = [];
            let notificationCount = 0;

            // Procesar notificaciones para documentos fiscales
            for (const doc of fiscalDocs) {
              let userEmail = doc.userEmail;
              let userName = doc.userName;
              
              // Si no tiene email, intentar obtenerlo del usuario
              if (doc.userId && !userEmail) {
                try {
                  const userDoc = await this.afs.collection('users').doc(doc.userId).get().toPromise();
                  if (userDoc?.exists) {
                    const userData = userDoc.data() as any;
                    userEmail = userData?.email || userData?.userEmail;
                    userName = userName || userData?.particular?.nombre || userData?.name || 'Usuario';
                  }
                } catch (error) {
                  console.error(`📧 [ERROR] Error al obtener datos del usuario ${doc.userId}:`, error);
                }
              }

              // Enviar notificaciones solo si tenemos email
              if (doc.userId && userEmail) {
                const expiryDate = typeof doc.expiryDate === 'object' && 'toDate' in doc.expiryDate 
                  ? (doc.expiryDate as any).toDate() 
                  : new Date(doc.expiryDate as any);
                
                console.log(`📧 [DEBUG] Preparando notificación para documento fiscal:`, {
                  userId: doc.userId,
                  userEmail: userEmail,
                  userName: userName,
                  fileName: doc.fileName
                });
                
                notificationCount++;
                notifications.push(
                  this.sendExpiredNotification(
                    doc.userId,
                    userEmail,
                    userName || 'Usuario',
                    doc.fileName || 'Documento fiscal',
                    expiryDate,
                    'fiscal'
                  )
                );
              } else {
                console.warn(`📧 [WARN] Documento fiscal sin userId o userEmail:`, {
                  docId: doc.id,
                  userId: doc.userId,
                  userEmail: userEmail
                });
              }
            }

            // Procesar notificaciones para cambios de residencia
            for (const doc of residenceDocs) {
              let userEmail = doc.userEmail;
              let userName = doc.userName;
              
              // Si no tiene email, intentar obtenerlo del usuario
              if (doc.userId && !userEmail) {
                try {
                  const userDoc = await this.afs.collection('users').doc(doc.userId).get().toPromise();
                  if (userDoc?.exists) {
                    const userData = userDoc.data() as any;
                    userEmail = userData?.email || userData?.userEmail;
                    userName = userName || userData?.particular?.nombre || userData?.name || 'Usuario';
                  }
                } catch (error) {
                  console.error(`📧 [ERROR] Error al obtener datos del usuario ${doc.userId}:`, error);
                }
              }

              // Enviar notificaciones solo si tenemos email
              if (doc.userId && userEmail) {
                const validityDate = typeof doc.validityDate === 'object' && 'toDate' in doc.validityDate 
                  ? (doc.validityDate as any).toDate() 
                  : new Date(doc.validityDate as any);
                
                console.log(`📧 [DEBUG] Preparando notificación para cambio de residencia:`, {
                  userId: doc.userId,
                  userEmail: userEmail,
                  userName: userName,
                  requestedCountry: doc.requestedCountry
                });
                
                notificationCount++;
                notifications.push(
                  this.sendExpiredNotification(
                    doc.userId,
                    userEmail,
                    userName || 'Usuario',
                    `Cambio de residencia a ${doc.requestedCountry || 'país no especificado'}`,
                    validityDate,
                    'residence_change'
                  )
                );
              } else {
                console.warn(`📧 [WARN] Cambio de residencia sin userId o userEmail:`, {
                  docId: doc.id,
                  userId: doc.userId,
                  userEmail: userEmail
                });
              }
            }

            console.log(`📧 [DEBUG] Total de notificaciones a enviar: ${notificationCount}`);

            if (notifications.length === 0) {
              console.log('📧 [INFO] No hay notificaciones para enviar');
              return;
            }

            console.log(`📧 [DEBUG] Enviando ${notifications.length} notificaciones...`);
            await Promise.all(notifications);
            console.log('📧 [DEBUG] Todas las notificaciones procesadas');
          }),
          map(() => undefined)
        );
      })
    );
  }

  /**
   * Enviar notificación de caducidad (próximos a caducar)
   */
  async sendExpiryNotification(
    userEmail: string, 
    userName: string, 
    documentName: string, 
    expiryDate: Date
  ): Promise<void> {
    try {
      const token = await this.authService.getJWTToken();
      const emailData = {
        to: userEmail,
        emailType: 'expiring' as const,
        data: {
          userName,
          documentName,
          expiryDate: expiryDate.toLocaleDateString('es-ES'),
          documentType: 'documento fiscal'
        }
      };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      console.log(`📧 [DEBUG] Enviando correo de recordatorio a ${userEmail}`);
      console.log(`📧 [DEBUG] URL: ${this.apiUrl}/notifications/fiscal-certificate-email`);

      await firstValueFrom(
        this.http.post(`${this.apiUrl}/notifications/fiscal-certificate-email`, emailData, { headers }).pipe(
          catchError((error: any) => {
            // Si es un error de conexión o endpoint no encontrado, mostrar mensaje informativo
            if (error?.status === 0 || 
                error?.status === 404 || 
                error?.message?.includes('ERR_CONNECTION_REFUSED')) {
              const reason = error?.status === 404 
                ? 'Endpoint de correo no configurado' 
                : 'Servicio de correo no disponible';
              console.warn(`⚠️ [INFO] ${reason}. El correo no se pudo enviar, pero el proceso continúa.`);
            } else {
              console.error('📧 [ERROR] Error al enviar correo de recordatorio:', error);
              console.error('📧 [ERROR] Detalles:', {
                message: error?.message,
                status: error?.status,
                statusText: error?.statusText,
                error: error?.error
              });
            }
            // Retornar un Observable vacío para no propagar el error
            return of(null);
          })
        )
      );
      console.log(`📧 [SUCCESS] Correo de recordatorio enviado exitosamente a ${userEmail}`);
    } catch (error: any) {
      // Captura adicional por si acaso, aunque catchError debería manejar todo
      if (error?.status === 0 || 
          error?.status === 404 || 
          error?.message?.includes('ERR_CONNECTION_REFUSED')) {
        const reason = error?.status === 404 
          ? 'Endpoint de correo no configurado' 
          : 'Servicio de correo no disponible';
        console.warn(`⚠️ [INFO] ${reason}. El correo no se pudo enviar, pero el proceso continúa.`);
      } else {
        console.error('📧 [ERROR] Error al enviar correo de recordatorio:', error);
      }
      // No lanzar error para no interrumpir el flujo
    }
  }

  /**
   * Enviar notificación de certificado caducado (correo + notificación interna)
   */
  private async sendExpiredNotification(
    userId: string,
    userEmail: string,
    userName: string,
    documentName: string,
    expiryDate: Date,
    documentType: 'fiscal' | 'residence_change'
  ): Promise<void> {
    console.log(`📧 [DEBUG] Enviando notificación para: ${userEmail} - ${documentName}`);
    
    try {
      // 1. Enviar correo electrónico
      const token = await this.authService.getJWTToken();
      const emailData = {
        to: userEmail,
        emailType: 'expired' as const,
        data: {
          userName,
          documentName,
          expiryDate: expiryDate.toLocaleDateString('es-ES'),
          documentType: documentType === 'fiscal' ? 'documento fiscal' : 'cambio de residencia'
        }
      };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      console.log(`📧 [DEBUG] URL de API: ${this.apiUrl}/notifications/fiscal-certificate-email`);
      console.log(`📧 [DEBUG] Datos del correo:`, emailData);

      try {
        const response = await firstValueFrom(
          this.http.post(`${this.apiUrl}/notifications/fiscal-certificate-email`, emailData, { headers }).pipe(
            catchError((emailError: any) => {
              // Si es un error de conexión o endpoint no encontrado, mostrar mensaje informativo
              if (emailError?.status === 0 || 
                  emailError?.status === 404 || 
                  emailError?.message?.includes('ERR_CONNECTION_REFUSED')) {
                const reason = emailError?.status === 404 
                  ? 'Endpoint de correo no configurado' 
                  : 'Servicio de correo no disponible';
                console.warn(`⚠️ [INFO] ${reason}. El correo a ${userEmail} no se pudo enviar, pero se creará la notificación interna.`);
              } else {
                console.error(`📧 [ERROR] Error al enviar correo a ${userEmail}:`, emailError);
                console.error(`📧 [ERROR] Detalles del error:`, {
                  message: emailError?.message,
                  status: emailError?.status,
                  statusText: emailError?.statusText,
                  error: emailError?.error
                });
              }
              // Retornar un Observable vacío para no propagar el error
              return of(null);
            })
          )
        );
        if (response) {
          console.log(`📧 [SUCCESS] Correo enviado exitosamente a ${userEmail}:`, response);
        }
      } catch (emailError: any) {
        // Captura adicional por si acaso, aunque catchError debería manejar todo
        if (emailError?.status === 0 || 
            emailError?.status === 404 || 
            emailError?.message?.includes('ERR_CONNECTION_REFUSED')) {
          const reason = emailError?.status === 404 
            ? 'Endpoint de correo no configurado' 
            : 'Servicio de correo no disponible';
          console.warn(`⚠️ [INFO] ${reason}. El correo a ${userEmail} no se pudo enviar, pero se creará la notificación interna.`);
        } else {
          console.error(`📧 [ERROR] Error al enviar correo a ${userEmail}:`, emailError);
        }
        // Continuar aunque falle el correo para crear la notificación interna
      }

      // 2. Crear notificación interna
      const notificationData = {
        title: 'Certificado fiscal caducado',
        message: `Tu ${documentType === 'fiscal' ? 'documento fiscal' : 'cambio de residencia'} "${documentName}" ha caducado. Por favor, sube un nuevo certificado.`,
        type: 'fiscal_expired',
        read: false,
        timestamp: firebase.firestore.Timestamp.now(),
        actionUrl: '/my-profile/residencia-fiscal',
        documentType: documentType,
        documentName: documentName
      };

      try {
        await this.afs.collection('user-notifications')
          .doc(userId)
          .collection('notifications')
          .add(notificationData);
        console.log(`📧 [SUCCESS] Notificación interna creada para usuario ${userId}`);
      } catch (notifError) {
        console.error(`📧 [ERROR] Error al crear notificación interna para usuario ${userId}:`, notifError);
      }

    } catch (error) {
      console.error(`📧 [ERROR] Error general al enviar notificación para ${userEmail}:`, error);
      // No lanzar error para no interrumpir el flujo
    }
  }

  /**
   * Aprobar solicitud de cambio con fecha de expiración
   */
  approveChangeRequestWithExpiry(documentId: string, expiryDate: Date): Observable<void> {
    return from(this.afs.collection(this.COLLECTION_NAME)
      .doc(documentId)
      .update({
        status: 'change_approved',
        expiryDate: firebase.firestore.Timestamp.fromDate(expiryDate),
        approvalDate: firebase.firestore.Timestamp.now()
      }));
  }

  /**
   * Rechazar solicitud de cambio
   */
  rejectChangeRequest(documentId: string, rejectionReason: string): Observable<void> {
    return from(this.afs.collection(this.COLLECTION_NAME)
      .doc(documentId)
      .update({
        status: 'change_rejected',
        rejectionReason: rejectionReason,
        rejectionDate: firebase.firestore.Timestamp.now()
      }));
  }
}
